import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from '../../utils/authUtils';
import AdminRouteGuard from '../AdminRouteGuard';
import { ADMIN_SECTIONS } from '../../utils/adminPermissions';
import axios from 'axios';

const BACKEND_URL = API_URL;

const TIER_COLORS = {
  community: 'bg-gray-100 text-gray-700',
  professional: 'bg-blue-100 text-blue-700',
  leader: 'bg-indigo-100 text-indigo-700',
  fellow: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  comped: 'bg-purple-100 text-purple-700',
  expired: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  free: 'bg-gray-100 text-gray-600',
};

const LOG_STATUS_COLORS = {
  pending: 'bg-orange-100 text-orange-700',
  auto_granted: 'bg-green-100 text-green-700',
  manually_approved: 'bg-blue-100 text-blue-700',
  dismissed: 'bg-gray-100 text-gray-500',
  failed: 'bg-red-100 text-red-700',
};

const AdminMembershipManager = () => {
  const navigate = useNavigate();

  // Tiers data (for dropdowns)
  const [tiers, setTiers] = useState([]);

  // Grant form state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTierSlug, setSelectedTierSlug] = useState('');
  const [grantStatus, setGrantStatus] = useState('comped');
  const [expiresAt, setExpiresAt] = useState('');
  const [cohortYear, setCohortYear] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const searchRef = useRef(null);

  // Memberships list state
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsTotal, setMembershipsTotal] = useState(0);
  const [membershipsPage, setMembershipsPage] = useState(1);
  const [tableSearch, setTableSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const PAGE_SIZE = 20;

  // Payment logs (Pending Upgrades) state
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [paymentLogsLoading, setPaymentLogsLoading] = useState(false);
  const [paymentLogsFilter, setPaymentLogsFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [approveModal, setApproveModal] = useState(null);
  const [approveTierSlug, setApproveTierSlug] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [dismissNotes, setDismissNotes] = useState('');
  const [approveAlumniSearch, setApproveAlumniSearch] = useState('');
  const [approveAlumniResults, setApproveAlumniResults] = useState([]);
  const [approveSelectedAlumni, setApproveSelectedAlumni] = useState(null);
  const [approveCreateMode, setApproveCreateMode] = useState(false);
  const [newAccountFirst, setNewAccountFirst] = useState('');
  const [newAccountLast, setNewAccountLast] = useState('');
  const [newAccountEmail, setNewAccountEmail] = useState('');

  // Superadmin check
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Debounce timer ref
  const searchTimerRef = useRef(null);

  // ========================================
  // DATA FETCHING
  // ========================================

  const getAuthHeaders = useCallback(() => {
    const token = getValidToken();
    return token ? { headers: { 'Authorization': `Bearer ${token}` } } : null;
  }, []);

  const fetchTiers = useCallback(async () => {
    const auth = getAuthHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/membership/tiers`, auth);
      setTiers(res.data);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    }
  }, [getAuthHeaders]);

  const fetchMemberships = useCallback(async (page = 1) => {
    const auth = getAuthHeaders();
    if (!auth) return;
    setMembershipsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/membership/memberships?page=${page}&page_size=${PAGE_SIZE}`;
      if (tableSearch) url += `&search=${encodeURIComponent(tableSearch)}`;
      if (tierFilter) url += `&tier_filter=${encodeURIComponent(tierFilter)}`;
      if (statusFilter) url += `&status_filter=${encodeURIComponent(statusFilter)}`;
      const res = await axios.get(url, auth);
      setMemberships(res.data.items || []);
      setMembershipsTotal(res.data.total || 0);
      setMembershipsPage(page);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setMembershipsLoading(false);
    }
  }, [getAuthHeaders, tableSearch, tierFilter, statusFilter]);

  const fetchPaymentLogs = useCallback(async (statusVal) => {
    const auth = getAuthHeaders();
    if (!auth) return;
    setPaymentLogsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/membership/payment-logs?page=1&page_size=50`;
      if (statusVal && statusVal !== 'all') url += `&status=${encodeURIComponent(statusVal)}`;
      const res = await axios.get(url, auth);
      setPaymentLogs(res.data.items || []);
      setPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Error fetching payment logs:', err);
    } finally {
      setPaymentLogsLoading(false);
    }
  }, [getAuthHeaders]);

  // Initial load
  useEffect(() => {
    fetchTiers();
    fetchMemberships(1);
    fetchPaymentLogs('pending');
    // Check superadmin status
    const auth = getAuthHeaders();
    if (auth) {
      axios.get(`${BACKEND_URL}/api/admin/me`, auth)
        .then(res => setIsSuperAdmin(res.data?.is_super_admin || false))
        .catch(() => {});
    }
  }, [fetchTiers, fetchMemberships, fetchPaymentLogs]);

  // Re-fetch memberships when filters change
  useEffect(() => {
    fetchMemberships(1);
  }, [tableSearch, tierFilter, statusFilter, fetchMemberships]);

  // Re-fetch payment logs when filter tab changes
  useEffect(() => {
    fetchPaymentLogs(paymentLogsFilter);
  }, [paymentLogsFilter, fetchPaymentLogs]);

  // ========================================
  // ALUMNI SEARCH (for grant form)
  // ========================================

  const searchAlumni = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const auth = getAuthHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/alumni/directory/search?q=${encodeURIComponent(query)}&limit=10`,
        auth
      );
      setSearchResults(res.data.alumni || []);
    } catch (err) {
      console.error('Error searching alumni:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchAlumni(value), 300);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTierSlug('');
    setGrantStatus('comped');
    setExpiresAt('');
    setCohortYear('');
  };

  // ========================================
  // GRANT MEMBERSHIP
  // ========================================

  const handleGrant = async () => {
    if (!selectedUser || !selectedTierSlug) return;
    setGrantLoading(true);
    setErrorMessage('');
    try {
      const auth = getAuthHeaders();
      if (!auth) return;
      const body = {
        alumni_user_id: selectedUser.id,
        tier_slug: selectedTierSlug,
        status: grantStatus,
      };
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      if (cohortYear) body.cohort_year = parseInt(cohortYear);

      await axios.post(`${BACKEND_URL}/api/admin/membership/grant`, body, auth);

      setSuccessMessage(`Membership granted to ${selectedUser.first_name} ${selectedUser.last_name}.`);
      setSelectedUser(null);
      setSelectedTierSlug('');
      setExpiresAt('');
      setCohortYear('');
      fetchMemberships(1);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error granting membership:', err);
      setErrorMessage(err.response?.data?.detail || 'Failed to grant membership.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setGrantLoading(false);
    }
  };

  // ========================================
  // REVOKE MEMBERSHIP
  // ========================================

  const handleRevoke = async (membership) => {
    const name = membership.alumni_name || 'this user';
    if (!window.confirm(`Revoke membership for ${name}?`)) return;
    try {
      const auth = getAuthHeaders();
      if (!auth) return;
      await axios.post(
        `${BACKEND_URL}/api/admin/membership/grant`,
        { alumni_user_id: membership.alumni_user_id, tier_slug: membership.tier_slug, status: 'cancelled' },
        auth
      );
      setSuccessMessage(`Membership revoked for ${name}.`);
      fetchMemberships(membershipsPage);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error revoking membership:', err);
      setErrorMessage('Failed to revoke membership.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // ========================================
  // PAYMENT LOG ACTIONS
  // ========================================

  const handleApproveLog = async () => {
    if (!approveModal || !approveTierSlug) return;
    if (approveCreateMode && (!newAccountFirst || !newAccountLast || !newAccountEmail)) return;
    if (!approveCreateMode && !approveSelectedAlumni) return;
    setApproveLoading(true);
    try {
      const auth = getAuthHeaders();
      if (!auth) return;
      const body = {
        tier_slug: approveTierSlug,
        status: 'active',
      };
      if (approveCreateMode) {
        body.create_account = {
          first_name: newAccountFirst,
          last_name: newAccountLast,
          email: newAccountEmail,
        };
      } else {
        body.alumni_user_id = approveSelectedAlumni?.id || approveModal.matched_alumni_user_id;
      }
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/membership/payment-logs/${approveModal.id}/approve`,
        body,
        auth
      );
      const msg = res.data.account_created
        ? `Account created for ${newAccountEmail} and membership granted.`
        : `Payment approved and membership granted for ${approveModal.customer_email}.`;
      setSuccessMessage(msg);
      setApproveModal(null);
      setApproveTierSlug('');
      setApproveAlumniSearch('');
      setApproveAlumniResults([]);
      setApproveSelectedAlumni(null);
      setApproveCreateMode(false);
      fetchPaymentLogs(paymentLogsFilter);
      fetchMemberships(1);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error approving payment log:', err);
      setApproveModal(null);
      setApproveTierSlug('');
      setApproveAlumniSearch('');
      setApproveAlumniResults([]);
      setApproveSelectedAlumni(null);
      setApproveCreateMode(false);
      setErrorMessage(err.response?.data?.detail || 'Failed to approve payment.');
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDismissLog = async (log) => {
    const notes = prompt('Optional: Add a note about why this is being dismissed:', '');
    if (notes === null) return; // cancelled
    try {
      const auth = getAuthHeaders();
      if (!auth) return;
      await axios.post(
        `${BACKEND_URL}/api/admin/membership/payment-logs/${log.id}/dismiss`,
        { notes },
        auth
      );
      setSuccessMessage(`Payment log dismissed for ${log.customer_email}.`);
      fetchPaymentLogs(paymentLogsFilter);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error dismissing payment log:', err);
      setErrorMessage('Failed to dismiss payment log.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const openApproveModal = (log) => {
    setApproveModal(log);
    setApproveTierSlug(log.matched_tier_slug || '');
    setDismissNotes('');
    setApproveAlumniSearch('');
    setApproveAlumniResults([]);
    setApproveSelectedAlumni(log.matched_alumni_user_id ? { id: log.matched_alumni_user_id, name: log.matched_alumni_name } : null);
    setApproveCreateMode(false);
    setNewAccountFirst('');
    setNewAccountLast('');
    setNewAccountEmail(log.customer_email || '');
  };

  const searchApproveAlumni = async (query) => {
    if (!query || query.length < 2) { setApproveAlumniResults([]); return; }
    const auth = getAuthHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/alumni/directory/search?q=${encodeURIComponent(query)}&limit=10`, auth);
      setApproveAlumniResults(res.data.alumni || []);
    } catch (err) {
      console.error('Error searching alumni for approve:', err);
    }
  };

  // ========================================
  // COMPUTED VALUES
  // ========================================

  const activeTiers = tiers.filter(t => t.is_active && t.tier_category === 'individual');
  const totalPages = Math.ceil(membershipsTotal / PAGE_SIZE);

  // Summary stats from current memberships view
  const stats = {
    total: membershipsTotal,
    active: memberships.filter(m => m.status === 'active').length,
    comped: memberships.filter(m => m.status === 'comped').length,
  };

  // Get current tier for selected user from memberships data
  const selectedUserCurrentTier = selectedUser
    ? memberships.find(m => m.alumni_user_id === selectedUser.id && ['active', 'comped'].includes(m.status))
    : null;

  const formatAmount = (cents, currency = 'USD') => {
    if (!cents) return '-';
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.MEMBERSHIP_MANAGER}>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Membership Manager</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Grant, upgrade, and manage alumni membership tiers</p>
              </div>
            </div>
            {isSuperAdmin && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/admin/outreach-manager')}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium"
                >
                  Outreach Manager
                </button>
                <button
                  onClick={() => navigate('/admin/crm')}
                  className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium"
                >
                  CRM
                </button>
                <button
                  onClick={() => navigate('/admin/executive-intelligence?tab=imports&section=unprovisioned')}
                  className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium"
                >
                  Research
                </button>
                <button
                  onClick={() => navigate('/admin/executive-intelligence?tab=brokerage')}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-xs font-medium"
                >
                  Brokerage
                </button>
                <button
                  onClick={() => navigate('/admin/swee')}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium"
                >
                  SWEE
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Success / Error Messages */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-800 text-sm font-medium">{successMessage}</span>
          </div>
        )}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800 text-sm font-medium">{errorMessage}</span>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="Total Members" value={membershipsTotal} color="amber" />
          <SummaryCard label="Active (Paid)" value={stats.active} color="green" />
          <SummaryCard label="Comped (Free)" value={stats.comped} color="purple" />
          <SummaryCard
            label="Tiers Available"
            value={activeTiers.length}
            color="blue"
          />
        </div>

        {/* ========================================
            PENDING UPGRADES SECTION
            ======================================== */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 border-orange-200 dark:border-orange-700">
          <div className="px-6 py-4 border-b border-orange-100 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-t-lg">
            <h2 className="text-lg font-semibold text-orange-800 dark:text-orange-300 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Pending Upgrades
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-orange-600 text-white">
                  {pendingCount}
                </span>
              )}
            </h2>
            <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
              Payment webhooks from HubSpot/Zapier. Auto-granted payments appear here as audit trail.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="px-6 py-3 border-b border-orange-100 dark:border-orange-800 flex flex-wrap gap-2">
            {[
              { key: 'pending', label: 'Pending' },
              { key: 'auto_granted', label: 'Auto-Granted' },
              { key: 'manually_approved', label: 'Approved' },
              { key: 'dismissed', label: 'Dismissed' },
              { key: 'all', label: 'All' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setPaymentLogsFilter(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentLogsFilter === tab.key
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50'
                }`}
              >
                {tab.label}
                {tab.key === 'pending' && pendingCount > 0 && paymentLogsFilter !== 'pending' && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold bg-orange-700 text-white">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Payment Logs Table */}
          <div className="overflow-x-auto">
            {paymentLogsLoading ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-6 h-6 animate-spin mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading payment logs...
              </div>
            ) : paymentLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {paymentLogsFilter === 'pending'
                  ? 'No pending payments. When alumni pay via HubSpot, webhooks will appear here.'
                  : `No ${paymentLogsFilter === 'all' ? '' : paymentLogsFilter.replace('_', ' ')} payment logs found.`}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Alumni Match</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paymentLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {log.customer_email || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {log.matched_alumni_name ? (
                          <span className="text-green-700 dark:text-green-400 font-medium">{log.matched_alumni_name}</span>
                        ) : (
                          <span className="text-orange-600 dark:text-orange-400 text-xs">No match</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatAmount(log.amount_cents, log.currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {log.matched_tier_name ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_COLORS[log.matched_tier_slug] || 'bg-gray-100 text-gray-700'}`}>
                            {log.matched_tier_name}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${LOG_STATUS_COLORS[log.status] || LOG_STATUS_COLORS.pending}`}>
                          {(log.status || 'pending').replace('_', ' ')}
                        </span>
                        {log.auto_granted && (
                          <svg className="w-3.5 h-3.5 text-green-500 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {log.status === 'pending' || log.status === 'failed' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openApproveModal(log)}
                              className="px-2.5 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDismissLog(log)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
                            >
                              Dismiss
                            </button>
                          </div>
                        ) : log.status === 'dismissed' && log.admin_notes ? (
                          <span className="text-xs text-gray-400 italic" title={log.admin_notes}>
                            {log.admin_notes.length > 30 ? log.admin_notes.substring(0, 30) + '...' : log.admin_notes}
                          </span>
                        ) : log.granted_membership_id ? (
                          <span className="text-xs text-green-600">Membership #{log.granted_membership_id}</span>
                        ) : (
                          <span className="text-xs text-gray-400">
                            {log.error_message ? (
                              <span title={log.error_message} className="cursor-help underline decoration-dotted">
                                Error
                              </span>
                            ) : '-'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Approve Modal */}
        {approveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Approve Payment & Grant Membership
              </h3>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{approveModal.customer_email}</span>
                </div>
                {approveModal.matched_alumni_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Alumni:</span>
                    <span className="font-medium text-green-700 dark:text-green-400">{approveModal.matched_alumni_name}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatAmount(approveModal.amount_cents, approveModal.currency)}
                  </span>
                </div>
                {approveModal.error_message && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-3">
                    <span className="text-xs text-orange-700 dark:text-orange-300">{approveModal.error_message}</span>
                  </div>
                )}
              </div>

              {/* Alumni selection: search existing OR create new account */}
              {!approveCreateMode ? (
                <>
                  {!approveSelectedAlumni ? (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Select Alumni *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={approveAlumniSearch}
                          onChange={(e) => { setApproveAlumniSearch(e.target.value); searchApproveAlumni(e.target.value); }}
                          placeholder="Search alumni by name or email..."
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                        />
                        {approveAlumniResults.length > 0 && (
                          <div className="absolute z-30 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {approveAlumniResults.map((u) => (
                              <button
                                key={u.id}
                                onClick={() => { setApproveSelectedAlumni(u); setApproveAlumniSearch(''); setApproveAlumniResults([]); }}
                                className="w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              >
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.first_name} {u.last_name}</div>
                                <div className="text-xs text-gray-500">{u.email || ''}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setApproveCreateMode(true)}
                        className="mt-2 text-xs text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
                      >
                        No account found? Create new alumni account
                      </button>
                    </div>
                  ) : (
                    <div className="mb-4 flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-3 py-2">
                      <div>
                        <span className="text-sm font-medium text-green-800 dark:text-green-300">
                          {approveSelectedAlumni.name || `${approveSelectedAlumni.first_name || ''} ${approveSelectedAlumni.last_name || ''}`.trim()}
                        </span>
                        {approveSelectedAlumni.email && (
                          <span className="text-xs text-green-600 dark:text-green-400 ml-2">{approveSelectedAlumni.email}</span>
                        )}
                      </div>
                      <button onClick={() => setApproveSelectedAlumni(null)} className="text-gray-400 hover:text-gray-600 text-xs">Change</button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mb-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-orange-800 dark:text-orange-300">Create New Alumni Account</span>
                    <button
                      onClick={() => setApproveCreateMode(false)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Back to search
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newAccountFirst}
                      onChange={(e) => setNewAccountFirst(e.target.value)}
                      placeholder="First name *"
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      value={newAccountLast}
                      onChange={(e) => setNewAccountLast(e.target.value)}
                      placeholder="Last name *"
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <input
                    type="email"
                    value={newAccountEmail}
                    onChange={(e) => setNewAccountEmail(e.target.value)}
                    placeholder="Email address *"
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    A temporary password will be generated. The user can reset it via email.
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Grant Tier *
                </label>
                <select
                  value={approveTierSlug}
                  onChange={(e) => setApproveTierSlug(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Select tier...</option>
                  {activeTiers.map((tier) => (
                    <option key={tier.slug} value={tier.slug}>
                      {tier.name} (Level {tier.level}{tier.price_cents ? ` · $${(tier.price_cents / 100).toLocaleString()}/yr` : ' · Free'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setApproveModal(null); setApproveTierSlug(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveLog}
                  disabled={!approveTierSlug || approveLoading || (approveCreateMode ? (!newAccountFirst || !newAccountLast || !newAccountEmail) : !approveSelectedAlumni)}
                  className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {approveLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Approving...
                    </>
                  ) : approveCreateMode ? (
                    'Create Account & Grant'
                  ) : (
                    'Approve & Grant'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grant / Upgrade Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 border-amber-200 dark:border-amber-700">
          <div className="px-6 py-4 border-b border-amber-100 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-t-lg">
            <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Grant or Upgrade Membership
            </h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Alumni Search */}
            {!selectedUser ? (
              <div className="relative" ref={searchRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Alumni
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  placeholder="Search by name or email (min 2 characters)..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-gray-700 dark:text-gray-100 text-sm"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-9 text-gray-400">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className="w-full text-left px-4 py-3 hover:bg-amber-50 dark:hover:bg-amber-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email || user.university_email || ''} {user.current_employer ? `· ${user.current_employer}` : user.university ? `· ${user.university}` : `· ID: ${user.id}`}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 text-sm text-gray-500 text-center">
                    No alumni found
                  </div>
                )}
              </div>
            ) : (
              /* Selected User Card */
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedUser.email || selectedUser.university_email || `ID: ${selectedUser.id}`}
                      {selectedUser.current_employer && ` · ${selectedUser.current_employer}`}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Current tier:</span>
                      {selectedUserCurrentTier ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_COLORS[selectedUserCurrentTier.tier_slug] || TIER_COLORS.community}`}>
                          {selectedUserCurrentTier.tier_name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Community (Free)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedUser(null); setSearchQuery(''); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Grant Form Fields */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Tier *
                    </label>
                    <select
                      value={selectedTierSlug}
                      onChange={(e) => setSelectedTierSlug(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Select tier...</option>
                      {activeTiers.map((tier) => (
                        <option key={tier.slug} value={tier.slug}>
                          {tier.name} (Level {tier.level}{tier.price_cents ? ` · $${(tier.price_cents / 100).toLocaleString()}/yr` : ' · Free'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status *
                    </label>
                    <select
                      value={grantStatus}
                      onChange={(e) => setGrantStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="comped">Comped (Free)</option>
                      <option value="active">Active (Paid)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Expires (optional)
                    </label>
                    <input
                      type="date"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>

                  {selectedTierSlug === 'fellow' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cohort Year
                      </label>
                      <input
                        type="number"
                        value={cohortYear}
                        onChange={(e) => setCohortYear(e.target.value)}
                        placeholder={new Date().getFullYear().toString()}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleGrant}
                    disabled={!selectedTierSlug || grantLoading}
                    className="px-6 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg text-sm transition-colors flex items-center gap-2"
                  >
                    {grantLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Granting...
                      </>
                    ) : (
                      'Grant Membership'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Memberships Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Active Memberships</h2>
          </div>

          {/* Filters */}
          <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 items-center">
            <input
              type="text"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-64 dark:bg-gray-700 dark:text-gray-100"
            />
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">All Tiers</option>
              {activeTiers.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="comped">Comped</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {membershipsTotal} total
            </span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {membershipsLoading ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-6 h-6 animate-spin mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading memberships...
              </div>
            ) : memberships.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {tableSearch || tierFilter || statusFilter
                  ? 'No memberships match your filters.'
                  : 'No memberships granted yet. Use the form above to grant the first one.'}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Granted By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Starts</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {memberships.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {m.alumni_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.alumni_email || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_COLORS[m.tier_slug] || TIER_COLORS.community}`}>
                          {m.tier_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[m.status] || STATUS_COLORS.free}`}>
                          {m.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.granted_by || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.starts_at ? new Date(m.starts_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.expires_at ? new Date(m.expires_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {['active', 'comped'].includes(m.status) && (
                          <button
                            onClick={() => handleRevoke(m)}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {membershipsPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchMemberships(membershipsPage - 1)}
                  disabled={membershipsPage <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchMemberships(membershipsPage + 1)}
                  disabled={membershipsPage >= totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </AdminRouteGuard>
  );
};

// ========================================
// SUMMARY CARD COMPONENT
// ========================================

const SummaryCard = ({ label, value, color }) => {
  const colorClasses = {
    amber: 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700',
    green: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700',
    purple: 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700',
    blue: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700',
  };
  const textClasses = {
    amber: 'text-amber-700 dark:text-amber-300',
    green: 'text-green-700 dark:text-green-300',
    purple: 'text-purple-700 dark:text-purple-300',
    blue: 'text-blue-700 dark:text-blue-300',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color] || colorClasses.amber}`}>
      <div className={`text-2xl font-bold ${textClasses[color] || textClasses.amber}`}>{value}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{label}</div>
    </div>
  );
};

export default AdminMembershipManager;
